import { Edit as EditIcon, Email as EmailIcon, Person as PersonIcon, Phone as PhoneIcon } from "@mui/icons-material";
import { Avatar, Box, Card, CardContent, IconButton, Stack, Typography } from "@mui/material";
import { useState, type FC } from "react";
import { useCustomerWithId } from "..//customers/useCustomerWithId";
import { useAgentWithId } from "../admin/agents/useAgentWithId";
import dayjs from "../dayjs";
import type { SavedOrder } from "../orders/orderTypes";
import type { CustomerContactData } from "./CustomerContactDialog";
import { CustomerContactDialog } from "./CustomerContactDialog";

interface Props {
  readonly supervisorId: string | undefined;
  readonly customerId: string;
  readonly order: {
    readonly id: number;
    readonly name: string;
    readonly startDate: string;
    readonly customerContactPersonEmail?: string;
  };
  readonly onUpdateContactPerson: (contactPersonEmail: string) => Promise<SavedOrder>;
}

const SupervisorInfo: FC<Props> = ({ supervisorId, customerId, order, onUpdateContactPerson }) => {
  const { customer } = useCustomerWithId(customerId);
  const { data: agent } = useAgentWithId(supervisorId ?? customer?.accountManager?.id);
  const customerContactPersonEmail = order.customerContactPersonEmail;
  const contactPerson = customer?.contacts?.find((c) => c.loginEmail == customerContactPersonEmail);
  const [customerContactData, setCustomerContactData] = useState<CustomerContactData>();

  const startDate = dayjs(order.startDate).format("DD.MM.YYYY");
  return (
    <Box sx={{ width: { xs: "100%", md: "50%" }, display: "flex" }}>
      <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, color: "text.secondary", alignItems: "center" }}>
            <PersonIcon fontSize="small" />
            <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
              Dane i kontakt
            </Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Opiekun Zamówienia
              </Typography>
              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: "500" }}>
                {agent?.name}
              </Typography>
              {agent && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.light" }}>
                      <EmailIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography
                      variant="body2"
                      component="a"
                      href={`mailto:${agent.contact.przezmorzeEmail}?subject=${encodeURIComponent(
                        `${order.id}: ${order.name} - ${startDate}`,
                      )}`}
                      sx={{
                        wordBreak: "break-all",
                        textDecoration: "none",
                        color: "text.primary",
                        borderBottom: "1px dashed rgba(0,0,0,0.3)",
                        transition: "all 0.2s ease",
                        "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                      }}
                    >
                      {agent.contact.przezmorzeEmail}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.light" }}>
                      <PhoneIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography
                      variant="body2"
                      component="a"
                      href={`tel:${agent.contact.phone?.replace(/\s+/g, "")}`}
                      sx={{
                        textDecoration: "none",
                        color: "text.primary",
                        borderBottom: "1px dashed rgba(0,0,0,0.3)",
                        transition: "all 0.2s ease",
                        "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                      }}
                    >
                      {agent.contact.phone}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Box>

            <Box sx={{ borderLeft: { xs: "none", sm: "1px solid rgba(0,0,0,0.08)" }, pl: { xs: 0, sm: 3 } }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Koordynator wyjazdu
                </Typography>

                <IconButton
                  size="small"
                  aria-label="Edytuj koordynatora wyjazdu"
                  onClick={() =>
                    setCustomerContactData({ selected: contactPerson, contacts: customer?.contacts ?? [] })
                  }
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": {
                      color: "primary.main",
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              <Typography variant="body1" sx={{ mt: 0.5, fontWeight: "500" }}>
                {contactPerson?.name}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: "rgba(0,0,0,0.06)" }}>
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </Avatar>
                  <Typography
                    variant="body2"
                    component="a"
                    href={`mailto:${contactPerson?.email}`}
                    sx={{
                      wordBreak: "break-all",
                      textDecoration: "none",
                      color: "text.primary",
                      borderBottom: "1px dashed rgba(0,0,0,0.3)",
                      transition: "all 0.2s ease",
                      "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                    }}
                  >
                    {contactPerson?.email}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: "rgba(0,0,0,0.06)" }}>
                    <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </Avatar>
                  <Typography
                    variant="body2"
                    component="a"
                    href={`tel:${contactPerson?.phone?.replace(/\s+/g, "")}`}
                    sx={{
                      textDecoration: "none",
                      color: "text.primary",
                      borderBottom: "1px dashed rgba(0,0,0,0.3)",
                      transition: "all 0.2s ease",
                      "&:hover": { color: "primary.main", borderColor: "primary.main", opacity: 0.8 },
                    }}
                  >
                    {contactPerson?.phone}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {customerContactData && (
        <CustomerContactDialog
          data={customerContactData}
          onUpdate={onUpdateContactPerson}
          onClose={() => setCustomerContactData(undefined)}
        />
      )}
    </Box>
  );
};

export default SupervisorInfo;
